import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import { MetricInfoMode } from '@databox/ember-integrations/lib/components/MetricInfoCard/MetricInfoCardEntryPoint';

import type { DSSetup, Service } from 'webapp-types';
import { BlockVisualization } from 'webapp/enums/enums';

import highlightResult from 'webapp/helpers/highlight-result';
import metricInfoAnchor from 'webapp/modifiers/metric-info-anchor';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type { Metric } from 'webapp/models-custom';

import type { NewBlockData } from 'webapp/services/model-manager/blocks';

export interface Args {
	model: Metric;
	typedFilter: string;
	onConnect: (sourceTypeKey: string) => void;
	addToBoard: (blockData: NewBlockData) => void;
}

export default class DesignerLeftPanelMetricsMetricComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	get sourcesText() {
		return this.args.model.sourcesText;
	}

	get showEditButton() {
		return !this.isDisabled && this.args.model.canEdit;
	}

	get showAddToBoardButton() {
		return !this.isDisabled;
	}

	@cached
	get isDisabled() {
		return this.args.model.missingBillingFeature || this.args.model.isPredefinedOnGenericSource;
	}

	get isFavorite() {
		return this.args.model.isFavorite;
	}

	get metricTypeForTracking() {
		let metricType = 'basic';
		if (this.args.model.isTypeMetricCalculationOrGeneric) {
			metricType = 'script';
		} else if (this.args.model.isTypeCustomQueryOrGeneric) {
			metricType = 'query';
		}
		return metricType;
	}

	get firstSourceAvatar() {
		return this.args.model.sourceAvatar;
	}

	@action
	edit() {
		const metric = this.args.model;
		if (metric) {
			metric.edit('designer');
		}
	}

	// The info card's Connect: the panel's own flow, which updates the list itself, so the card doesn't look it up again.
	@action
	connectFromCard(): Promise<boolean> {
		this.connect();
		return Promise.resolve(false);
	}

	@action
	addToBoard() {
		const metric = this.args.model;

		let promise: Promise<DSSetup.MetricSetupData>;

		if (metric.spaceAccessId) {
			promise = this.mmMetricsService.resolveGenericMetricWithSpecific(
				metric.key,
				metric.spaceAccessId,
			);
		} else {
			promise = Promise.resolve(metric.metricSetupData!);
		}

		void promise.then((metricSetupData) => {
			const data: NewBlockData = {
				fromMetricSetup: metricSetupData,
				metricType: this.metricTypeForTracking,
				visualization: BlockVisualization.BAR_CHART,
			};
			this.args.addToBoard(data);
		});
	}

	@action
	connect() {
		const sourceTypeKey = this.args.model.dataSourceTypeKey;
		if (sourceTypeKey) {
			this.args.onConnect(sourceTypeKey);
		}
	}
	<template>
		<li
			class="metric-item datablock {{if this.isDisabled 'disabled' 'ui-draggable'}}"
			data-metricsetupkey={{@model.metricData.key}}
			data-metricsetupspaceaccessid={{@model.metricData.spaceAccessId}}
			data-metrictype={{this.metricTypeForTracking}}
		>
			<div class="item__data-ct">
				<div class="item__sources">
					<img src={{this.firstSourceAvatar}} alt />
				</div>
				<div class="item__title">

					<strong class="text">
						<span>
							{{highlightResult text=@model.name match=@typedFilter}}
						</span>
					</strong>

					{{#if @model.isTypeMetricCalculationOrGeneric}}
						<CommonUiIconComponent @name="Calculator" @size="xs" @class="icon-type" />
					{{else if @model.isTypeCustomQueryOrGeneric}}
						<CommonUiIconComponent @name="Data" @size="xs" @class="icon-type" />
						{{#if @model.allowDimensions}}
							<CommonUiIconComponent
								@name="Breakdown"
								@size="sm"
								@class="icon-type"
								title="Breakdown by dimension(s) available."
							/>
						{{/if}}
					{{/if}}

					<VerificationBadgeComponent
						@verification={{@model.verificationInfo}}
						@entityLabel="metric"
						@size="sm"
					/>
					{{#if this.isFavorite}}
						<CommonUiIconComponent @name="StarFill" @size="sm" @class="icon-fav" />
					{{/if}}

				</div>
				<div class="item__subtitle">

					<span class="text">
						<span>
							{{highlightResult text=this.sourcesText match=@typedFilter}}
						</span>
					</span>

					{{#if @model.source.isStatusActionable}}
						<span class="status">
							<ToolTipWithIconComponent
								@text={{{@model.source.statusDescriptionWithLinks}}}
								@iconName="AlertFill"
								@maxWidth={{230}}
							/>
						</span>
					{{/if}}

				</div>
			</div>

			<div class="item__actions-ct">

				{{#if this.showEditButton}}
					<a
						role="button"
						class="mk-edit-button mk-edit-button--dark"
						{{on "click" this.edit}}
					>Edit
					</a>
				{{/if}}

				<span
					role="button"
					tabindex="0"
					class="info-button"
					aria-label="About {{@model.name}}"
					{{metricInfoAnchor
						@model
						trigger="click"
						mode=MetricInfoMode.FULL
						hideActions=true
						onConnect=this.connectFromCard
					}}
				><CommonUiIconComponent @name="InfoFill" @size="sm" />
				</span>

				{{#if this.showAddToBoardButton}}
					<a class="add" {{on "click" this.addToBoard}}>
						<CommonUiIconComponent @name="Plus" @size="md" />
					</a>
				{{/if}}

			</div>

			{{#unless this.isDisabled}}
				<ToolTipComponent
					@text="<img src='/images/arrow-drag.png' height='9'>Drag and drop a datablock to the canvas on the right."
					@position="right"
					@extraClass="drag-tooltip"
					@maxWidth={{185}}
					@delay={{2000}}
				/>
			{{/unless}}

		</li>
	</template>
}
